import { DataTablePresenter, isTabletPortraitDown, ITableColumn, PlaidAccountInfo, ProviderPlaidItemInfo, Variables } from '@enter/core';
import styled from '@emotion/styled/macro';
import { mdiArrowLeft, mdiPlus } from '@mdi/js';
import { Icon } from '@mdi/react';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { DialogContext } from 'services';
import { IObservedRootStore } from 'stores';
import * as S from './shared.styles';

const Badge = styled.span`
    display: inline-block;
    padding: 4px 12px;
    margin: 2px 4px 2px 0;
    background-color: ${Variables.Colors.b50};
    color: ${Variables.Colors.b500};
    border-radius: ${Variables.Rounded.medium};
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
`;

interface IBankingAccount {
    plaidItemId?: string;
    externalItemId?: string;
    externalInstitutionId?: string;
    institutionName?: string;
    accounts?: PlaidAccountInfo[];
}

const TextCell = ({ cell }: any): React.ReactElement => <S.TableCell title={cell.value}>{cell.value}</S.TableCell>;

const AccountsCell = ({ cell }: any): React.ReactElement => {
    const accounts: PlaidAccountInfo[] = cell.value || [];
    return (
        <S.TableCell>
            {accounts.map((account, index) => (
                <Badge key={index}>
                    {account.name} ({account.subtype}, ****{account.mask})
                </Badge>
            ))}
        </S.TableCell>
    );
};

const columns: ITableColumn<IBankingAccount>[] = [
    {
        id: 'institutionName',
        Header: 'Institution',
        accessor: 'institutionName',
        disableSortBy: true,
        Cell: TextCell,
    },
    {
        id: 'accounts',
        Header: 'Accounts',
        accessor: 'accounts',
        disableSortBy: true,
        Cell: AccountsCell,
    },
];

export const Banking: React.FunctionComponent<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const dialogContext = useContext(DialogContext);
            const [isDone, setDone] = useState(false);
            const [isLoading, setLoading] = useState(false);
            const [waitingFor, setWaitingFor] = useState<'add'>();
            const [linkToken, setLinkToken] = useState<string | null>(null);
            const [presenter] = useState(
                new DataTablePresenter({
                    name: 'banking',
                    data: [],
                    columns,
                    filterSchemas: [],
                }),
            );
            presenter.saveTableDefaults({
                ...presenter.getTableDefaults(),
                pageSize: 100,
            });

            useEffect(() => {
                if (isDone || isLoading) {
                    return;
                }
                (async () => {
                    setLoading(true);
                    const items = await rootStore.settingsStore.fetchPlaidBankingAccounts();
                    presenter.onDataUpdate(convertAccounts(items));
                    presenter.onDataTotalChange(items.length);
                    setDone(true);
                    setLoading(false);
                })();
            }, [isDone, isLoading, presenter, rootStore]);

            const convertAccounts = (items: ProviderPlaidItemInfo[]): IBankingAccount[] => {
                return _.map(items, item => ({
                    plaidItemId: item.plaidItemId,
                    externalItemId: item.externalItemId,
                    externalInstitutionId: item.externalInstitutionId,
                    institutionName: item.institutionName,
                    accounts: item.accounts,
                }));
            };

            const onSuccess = useCallback(
                async (publicToken: string, metadata: any) => {
                    try {
                        const institutionId = metadata?.institution?.institution_id;
                        await rootStore.settingsStore.connectPlaidAccount(publicToken, institutionId);
                        // Refresh the accounts list
                        setDone(false);
                        setWaitingFor(undefined);
                    } catch (err) {
                        dialogContext.showError(err);
                        setWaitingFor(undefined);
                    }
                },
                [rootStore, dialogContext],
            );

            const onExit = useCallback(
                (err: any, metadata: any) => {
                    if (err) {
                        dialogContext.showError(err);
                    }
                    setWaitingFor(undefined);
                },
                [dialogContext],
            );

            const config = {
                token: linkToken,
                onSuccess,
                onExit,
            };

            const { open, ready } = usePlaidLink(config);

            const addAccount = async () => {
                setWaitingFor('add');
                try {
                    const token = await rootStore.settingsStore.getPlaidLinkToken();
                    if (token) {
                        setLinkToken(token);
                    }
                } catch (err) {
                    dialogContext.showError(err);
                    setWaitingFor(undefined);
                }
            };

            useEffect(() => {
                if (linkToken && ready) {
                    open();
                }
            }, [linkToken, ready, open]);

            return (
                <S.SubCategoryCol>
                    <S.SubCategoryItem>
                        <S.SubCategoryItemRow>
                            <S.SubCategoryH1>
                                {isTabletPortraitDown() && (
                                    <S.ReturnLink to="/settings">
                                        {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                    </S.ReturnLink>
                                )}
                                Banking
                            </S.SubCategoryH1>
                            <S.SubCategoryAddButton
                                disabled={waitingFor === 'add'}
                                variant="primary"
                                onClick={addAccount}>
                                {waitingFor === 'add' ? (
                                    <S.Waiting size="18px" />
                                ) : (
                                    <S.SubCategoryPlus path={mdiPlus} size={1} />
                                )}{' '}
                                Create connection
                            </S.SubCategoryAddButton>
                        </S.SubCategoryItemRow>
                        <S.Table
                            presenter={presenter}
                            isLoading={isLoading}
                            hideFiltering={true}
                            hidePagination={true}
                            hideSettings={true}
                        />
                    </S.SubCategoryItem>
                </S.SubCategoryCol>
            );
        },
    ),
);
